import { useMemo } from "react";
import { DollarSign, TrendingUp, ShoppingCart, RotateCcw, Percent, CreditCard } from "lucide-react";
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, Line } from "recharts";
import { SummaryCard } from "./SummaryCard";
import type { ChartResponse } from "../../types";

interface RevenueSummary {
  total: number;
  subscriptions: number;
  iap: number;
  sales: number;
  returns: number;
  gross_total: number;
}

interface RevenueTabContentProps {
  loadingSummary: boolean;
  revenueSummary?: RevenueSummary;
  loadingRevenueTimeseries: boolean;
  revenueTimeseries?: ChartResponse;
  range: { start: string; end: string };
}

export function RevenueTabContent({
  loadingSummary,
  revenueSummary,
  loadingRevenueTimeseries,
  revenueTimeseries,
  range,
}: RevenueTabContentProps) {
  const periodLengthInDays = useMemo(() => {
    const startDate = new Date(range.start);
    const endDate = new Date(range.end);

    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return 0;
    }

    const diffMs = endDate.getTime() - startDate.getTime();
    return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1);
  }, [range.end, range.start]);

  const averageDailyRevenue = useMemo(() => {
    if (!revenueSummary || periodLengthInDays === 0) return 0;
    return revenueSummary.total / periodLengthInDays;
  }, [periodLengthInDays, revenueSummary]);

  const revenueShares = useMemo(() => {
    const total = revenueSummary?.total ?? 0;
    if (total <= 0) {
      return { subscriptions: 0, iap: 0, sales: 0 };
    }
    return {
      subscriptions: (revenueSummary.subscriptions / total) * 100,
      iap: (revenueSummary.iap / total) * 100,
      sales: (revenueSummary.sales / total) * 100,
    };
  }, [revenueSummary]);

  const returnsImpact = useMemo(() => {
    const gross = revenueSummary?.gross_total ?? 0;
    const returns = revenueSummary?.returns ?? 0;
    if (gross <= 0) return 0;
    return (returns / gross) * 100;
  }, [revenueSummary]);

  const revenueChartData = useMemo(() => {
    if (!revenueTimeseries?.labels || !revenueTimeseries?.series) return [];

    const netSeries = revenueTimeseries.series.find((s) => s.name === "Net Revenue");
    const grossSeries = revenueTimeseries.series.find((s) => s.name === "Gross Revenue");
    const returnsSeries = revenueTimeseries.series.find((s) => s.name === "Returns");

    return revenueTimeseries.labels.map((label, index) => ({
      date: new Date(label).toLocaleDateString("pt-BR", {
        month: "short",
        day: "numeric",
      }),
      net: netSeries?.values[index] ?? 0,
      gross: grossSeries?.values[index] ?? 0,
      returns: returnsSeries?.values[index] ?? 0,
    }));
  }, [revenueTimeseries]);

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 2,
      }),
    []
  );

  const compactCurrencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    []
  );

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <SummaryCard
          title="Total Revenue"
          value={revenueSummary?.total ?? 0}
          loading={loadingSummary}
          format="currency"
          icon={DollarSign}
          color="bg-[#22C55E33]"
          tooltip="Receita líquida total após dedução das taxas das lojas (App Store/Google Play). Valor que você realmente recebe."
        />
        <SummaryCard
          title="Gross Revenue"
          value={revenueSummary?.gross_total ?? 0}
          loading={loadingSummary}
          format="currency"
          icon={TrendingUp}
          color="bg-[#3B82F633]"
          tooltip="Receita bruta total antes da dedução das taxas das lojas. Valor total gerado pelas vendas."
        />
        <SummaryCard
          title="Avg Daily Revenue"
          value={averageDailyRevenue}
          loading={loadingSummary}
          format="currency"
          icon={Percent}
          color="bg-[#6366F133]"
          tooltip="Média diária da receita líquida considerando o período selecionado."
          footnote={
            !loadingSummary && periodLengthInDays
              ? `Período analisado: ${periodLengthInDays} dia${periodLengthInDays > 1 ? "s" : ""}`
              : undefined
          }
        />
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Evolução da Receita</h2>
        <div className="bg-[#232323] border-none rounded-lg">
          <div className="p-5">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-400" />
                Receita líquida x bruta
              </h3>
              <p className="text-sm text-white/60">
                Visualize a tendência da receita líquida, bruta e o impacto dos reembolsos no período selecionado.
              </p>
            </div>
            <div style={{ width: "100%", height: 400 }}>
              {loadingRevenueTimeseries ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Carregando dados...</p>
                </div>
              ) : revenueChartData.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Sem dados disponíveis para o período selecionado</p>
                </div>
              ) : (
                <ResponsiveContainer>
                  <LineChart data={revenueChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 12, fill: "#ffffff80" }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(value: number) => compactCurrencyFormatter.format(value)}
                    />
                    <RechartsTooltip
                      contentStyle={{
                        backgroundColor: "#232323",
                        border: "1px solid #ffffff20",
                        borderRadius: "8px",
                        color: "white",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
                      }}
                      formatter={(value: number | string, name: string) => [
                        currencyFormatter.format(Number(value)),
                        name,
                      ]}
                    />
                    <Legend wrapperStyle={{ color: "#ffffff80", fontSize: "12px" }} />
                    <Line
                      type="monotone"
                      dataKey="net"
                      name="Receita líquida"
                      stroke="#22C55E"
                      strokeWidth={3}
                      dot={{ fill: "#22C55E", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 6, stroke: "#22C55E", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="gross"
                      name="Receita bruta"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: "#3B82F6", strokeWidth: 2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="returns"
                      name="Reembolsos"
                      stroke="#EF4444"
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      dot={{ fill: "#EF4444", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 5, stroke: "#EF4444", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-xl font-semibold text-white mb-4">Revenue Mix</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <SummaryCard
            title="Subscriptions"
            value={revenueSummary?.subscriptions ?? 0}
            loading={loadingSummary}
            format="currency"
            icon={CreditCard}
            color="bg-[#22C55E33]"
            tooltip="Receita gerada por assinaturas recorrentes (mensais, anuais). Valor mais estável e previsível."
            footnote={!loadingSummary ? `Participação: ${revenueShares.subscriptions.toFixed(1)}% da receita líquida` : undefined}
          />
          <SummaryCard
            title="In-App Purchases"
            value={revenueSummary?.iap ?? 0}
            loading={loadingSummary}
            format="currency"
            icon={ShoppingCart}
            color="bg-[#3B82F633]"
            tooltip="Receita gerada por compras dentro do aplicativo (moedas, recursos premium, upgrades)."
            footnote={!loadingSummary ? `Participação: ${revenueShares.iap.toFixed(1)}% da receita líquida` : undefined}
          />
          <SummaryCard
            title="App Sales"
            value={revenueSummary?.sales ?? 0}
            loading={loadingSummary}
            format="currency"
            icon={DollarSign}
            color="bg-[#8B5CF633]"
            tooltip="Receita gerada pela venda inicial do aplicativo (preço de compra único)."
            footnote={!loadingSummary ? `Participação: ${revenueShares.sales.toFixed(1)}% da receita líquida` : undefined}
          />
          <SummaryCard
            title="Returns"
            value={revenueSummary?.returns ?? 0}
            loading={loadingSummary}
            format="currency"
            icon={RotateCcw}
            color="bg-[#EF444433]"
            tooltip="Valor total de reembolsos solicitados pelos usuários. Impacto negativo na receita."
            footnote={!loadingSummary ? `Impacto: ${returnsImpact.toFixed(1)}% da receita bruta` : undefined}
          />
        </div>
      </div>
    </div>
  );
}
